import React, { useState } from 'react';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Pagination, Skeleton, Table } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import FormlessField from 'packages/kakapo-ui-kit/components/forms/controls/FormlessField';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';

export default function SentEmailsListContainer() {
	const { t } = useTranslation();
	const [emailInput, setEmailInput] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [isSuppressed, setIsSuppressed] = useState<boolean>(false);

	const [pagination, setPagination] = useState<Pagination>({ last: 10 });

	const { data, loading } = graphql.useSentEmailsQuery({
		variables: {
			email: email === '' ? undefined : email,
			...pagination,
		},
	});

	const [checkSuppressedEmail, { loading: suppressedLoading }] =
		graphql.useCheckSuppressedEmailMutation();

	const [removeSuppressedEmail, { loading: removeSuppressedEmailLoading }] =
		graphql.useRemoveSuppressedEmailMutation();

	const checkIfSuppressed = async (newEmail?: string) => {
		if (newEmail) {
			const response = await checkSuppressedEmail({
				variables: {
					input: {
						email: newEmail,
					},
				},
			});

			setIsSuppressed(response.data?.checkSuppressedEmail.isEmailSuppressed ?? false);
		} else {
			setIsSuppressed(false);
		}
	};

	const confirmSearch = async (event: React.FormEvent) => {
		event.preventDefault();

		if (loading || suppressedLoading) {
			return;
		}

		setEmail(emailInput);

		checkIfSuppressed(emailInput);
	};

	return (
		<Box>
			<Box mb={3}>
				<SectionTitle>{t('common.settings.sentEmails.title')}</SectionTitle>
			</Box>

			<Box mb={3}>
				<form onSubmit={confirmSearch}>
					<Flex justifyContent="space-between">
						<Box width={6 / 12}>
							<Flex>
								<Box width={9 / 12}>
									<FormlessField
										type="email"
										value={emailInput}
										onChange={(event) => {
											setEmailInput(event.currentTarget.value);
										}}
									/>
								</Box>
								<Box width={3 / 12} pl={3}>
									<PrimaryButton type="submit" disabled={loading || suppressedLoading}>
										Search
									</PrimaryButton>
								</Box>
							</Flex>
						</Box>
						<Box width={6 / 12} pl={3}>
							<Flex alignItems="center" justifyContent="flex-end">
								{email && !suppressedLoading && (
									<Box mr={3}>
										<ParagraphSmall color={isSuppressed ? 'red' : 'slateBlue'}>
											User is{!isSuppressed ? ' not' : ''} on suppression list
										</ParagraphSmall>
									</Box>
								)}
								<PrimaryButton
									disabled={suppressedLoading || !isSuppressed || removeSuppressedEmailLoading}
									type="button"
									onClick={async () => {
										await removeSuppressedEmail({
											variables: {
												input: {
													email: email!,
												},
											},
										});

										setIsSuppressed(false);
									}}
								>
									Remove
								</PrimaryButton>
							</Flex>
						</Box>
					</Flex>
				</form>
			</Box>

			<Table>
				<Table.Header>
					<Table.Cell basis={0.3}>Sent at</Table.Cell>
					<Table.Cell basis={0.3}>Email Address</Table.Cell>
					<Table.Cell basis={0.4}>Subject</Table.Cell>
				</Table.Header>
				{data?.sentEmails.edges
					.slice()
					.reverse()
					.map((edge) => (
						<Table.Row key={edge.cursor}>
							<Table.Cell basis={0.3}>
								{moment(edge.node.sentAt).format('YYYY-MM-DD HH:mm')}
							</Table.Cell>
							<Table.Cell basis={0.3}>{edge.node.recipientEmail}</Table.Cell>
							<Table.Cell basis={0.4}>{edge.node.subject}</Table.Cell>
						</Table.Row>
					)) ?? (
					<Skeleton.Repeat count={10}>
						<Table.Row>
							<Table.Cell.Skeleton basis={0.3} />
							<Table.Cell.Skeleton basis={0.3} />
							<Table.Cell.Skeleton basis={0.4} />
						</Table.Row>
					</Skeleton.Repeat>
				)}
			</Table>
			<Table.Paging
				pageInfo={data?.sentEmails.pageInfo as graphql.PageInfo}
				onPaginate={setPagination}
			/>
		</Box>
	);
}
